// AudioManager — reusable, event-driven audio engine for casino games.
// Title-agnostic; Lucky Ducky, Fire Ball, Buffalo etc. all instantiate one
// with their own sound-pack. Assets can be swapped without touching game code.
//
// Contract:
//   const am = createAudioManager({ soundPack, muted, storageKey });
//   am.play('spin_start');
//   am.loop('reel_spin_loop');
//   am.stopLoop('reel_spin_loop');
//   am.setMuted(true);
//   am.unlock();  // call from a real user gesture (mobile audio unlock)
//
// Sound pack shape (all optional — missing events are silent):
//   { spin_start: '/audio/ld/spin_start.mp3', ... }
// A single event may also be:
//   { src: '...', volume: 0.7, poolSize: 3 }         // pooled one-shots
//   { src: '...', loop: true, volume: 0.5 }          // looped
//   { synth: (ctx) => { ... } }                      // WebAudio synth stub
//
// Mobile browsers: the constructor DOES NOT create AudioContext. It's
// created lazily on first `unlock()` (which must be a user gesture).

const DEFAULT_STORAGE_KEY = 'game-audio-muted';

// Global output ceiling applied to every sound (one-shots + loops + ducking
// targets).  Keeps the game from ever being blaring even at full device
// volume.  1.0 = raw pack volumes; lower = quieter overall.
const MASTER_VOLUME = 0.6;

function noop() {}

function synthTone(ctx, freq, dur = 0.08, type = 'sine', gain = 0.05) {
    if (!ctx) return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type; o.frequency.value = freq; g.gain.value = gain;
    o.connect(g); g.connect(ctx.destination);
    o.start();
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    o.stop(ctx.currentTime + dur);
}

// Filtered white-noise burst with an amplitude envelope — used for
// mechanical reel-stop "thunks" that feel percussive without any tonal
// beep character.  freqCut sets the low-pass corner so different reels
// can have subtly different weight (heavier stops = lower cutoff).
function synthNoiseBurst(ctx, {
    dur = 0.09,
    freqCut = 900,     // Hz
    resonance = 1.2,
    gain = 0.10,
    attack = 0.003,
    release = 0.07,
} = {}) {
    if (!ctx) return;
    const bufSize = Math.max(1, Math.floor(ctx.sampleRate * dur));
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) data[i] = (Math.random() * 2 - 1);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = freqCut;
    lp.Q.value = resonance;
    const g = ctx.createGain();
    const now = ctx.currentTime;
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(gain, now + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, now + attack + release);
    src.connect(lp); lp.connect(g); g.connect(ctx.destination);
    src.start(now);
    src.stop(now + dur + 0.02);
}

// Continuous "reel spinning" texture — filtered pink-noise-ish loop with
// slow amplitude wobble.  Returns a stop() handle so the AudioManager
// can tear it down cleanly at the exact moment the last reel settles.
// NO beep. NO click. NO tick.  Just soft mechanical airflow.
function synthReelLoop(ctx, { gain = 0.045, freqCut = 700 } = {}) {
    if (!ctx) return { stop: () => {} };
    // 2-second white-noise buffer, looped continuously.
    const bufSize = Math.floor(ctx.sampleRate * 2);
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const data = buf.getChannelData(0);
    // Pink-noise-ish: sum of decaying random walks so the texture has
    // more low-frequency energy than pure white noise (feels warmer,
    // less hissy on phone speakers).
    let last = 0;
    for (let i = 0; i < bufSize; i++) {
        const w = Math.random() * 2 - 1;
        last = last * 0.72 + w * 0.28;
        data[i] = last * 1.4;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;

    // Low-pass to remove hiss; band-pass Q gives a subtle "reel body" tone.
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = freqCut;
    lp.Q.value = 0.7;

    // Amp envelope: 40ms fade-in, then live-controllable steady gain.
    const g = ctx.createGain();
    const now = ctx.currentTime;
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(gain, now + 0.04);

    // Slow amplitude LFO so the loop feels alive, not static.
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 3.2;                // 3.2 Hz wobble
    lfoGain.gain.value = gain * 0.18;         // ~18% depth
    lfo.connect(lfoGain);
    lfoGain.connect(g.gain);

    src.connect(lp); lp.connect(g); g.connect(ctx.destination);
    src.start(now);
    lfo.start(now);

    return {
        stop: () => {
            try {
                const t = ctx.currentTime;
                g.gain.cancelScheduledValues(t);
                g.gain.setValueAtTime(g.gain.value, t);
                g.gain.exponentialRampToValueAtTime(0.0001, t + 0.08); // clean fade-out
                src.stop(t + 0.1);
                lfo.stop(t + 0.1);
            } catch { /* already stopped */ }
        },
    };
}

/**
 * Built-in synthesized fallback pack. Used when licensed audio assets are
 * not yet provisioned. Everything except the fanfare cues uses filtered
 * noise rather than square-wave beeps so the temporary audio feels
 * mechanical, not chiptune.  Every event is silent-by-default until
 * unlock() is called from a user gesture (mobile compliance).
 *
 * IMPORTANT: reel_spin_loop is a real WebAudio noise-based loop with a
 * clean fade-in/fade-out — NOT a repeating short tone.  The previous
 * implementation replayed a 200 Hz square wave every 120 ms which
 * produced the "beep beep beep" the previous audio pass exhibited.
 */
export const FALLBACK_SYNTH_PACK = {
    ui_button:         { synth: (c) => synthTone(c, 720, 0.03, 'triangle', 0.04) },
    bet_change:        { synth: (c) => synthTone(c, 540, 0.05, 'triangle', 0.05) },
    // Spin-start: brief soft "whoosh" (noise burst with wide low-pass).
    spin_start:        { synth: (c) => synthNoiseBurst(c, { dur: 0.18, freqCut: 1400, resonance: 0.8, gain: 0.09, attack: 0.006, release: 0.16 }) },
    // Continuous reel-spin loop — persistent WebAudio graph, NOT a timed
    // synth callback.  The AudioManager's loop() path detects `.persistent`
    // and starts/stops the returned handle instead of polling.
    reel_spin_loop:    { persistent: (c) => synthReelLoop(c, { gain: 0.05, freqCut: 720 }), loop: true },
    // Reel-stop cues — five graduated noise thunks (heavier → lighter)
    // so the ear can distinguish each reel's landing.  No square waves.
    reel_stop_1:       { synth: (c) => synthNoiseBurst(c, { dur: 0.10, freqCut: 520, resonance: 1.4, gain: 0.14, attack: 0.002, release: 0.08 }) },
    reel_stop_2:       { synth: (c) => synthNoiseBurst(c, { dur: 0.10, freqCut: 560, resonance: 1.4, gain: 0.14, attack: 0.002, release: 0.08 }) },
    reel_stop_3:       { synth: (c) => synthNoiseBurst(c, { dur: 0.10, freqCut: 620, resonance: 1.4, gain: 0.14, attack: 0.002, release: 0.08 }) },
    reel_stop_4:       { synth: (c) => synthNoiseBurst(c, { dur: 0.10, freqCut: 680, resonance: 1.4, gain: 0.14, attack: 0.002, release: 0.08 }) },
    reel_stop_5:       { synth: (c) => synthNoiseBurst(c, { dur: 0.11, freqCut: 750, resonance: 1.4, gain: 0.15, attack: 0.002, release: 0.09 }) },
    win_small:         { synth: (c) => { synthTone(c, 660, 0.09); setTimeout(() => synthTone(c, 880, 0.12), 90); } },
    win_medium:        { synth: (c) => { [660, 880, 990].forEach((f, i) => setTimeout(() => synthTone(c, f, 0.11, 'sine', 0.06), i * 90)); } },
    win_big:           { synth: (c) => { [523, 659, 784, 988].forEach((f, i) => setTimeout(() => synthTone(c, f, 0.14, 'triangle', 0.07), i * 110)); } },
    win_mega:          { synth: (c) => { [523, 659, 784, 988, 1174, 1319].forEach((f, i) => setTimeout(() => synthTone(c, f, 0.16, 'triangle', 0.08), i * 120)); } },
    win_countup:       { synth: (c) => synthTone(c, 900, 0.04, 'sine',    0.03) },
    bonus_tease:       { synth: (c) => synthTone(c, 440, 0.20, 'sawtooth', 0.05) },
    bonus_trigger:     { synth: (c) => { [523, 659, 784].forEach((f, i) => setTimeout(() => synthTone(c, f, 0.20), i * 130)); } },
    beto_respins_enter:{ synth: (c) => { [523, 784, 1046].forEach((f, i) => setTimeout(() => synthTone(c, f, 0.24, 'triangle', 0.08), i * 140)); } },
    beto_respin:       { synth: (c) => synthTone(c, 440, 0.10, 'square',   0.05) },
    beto_retrigger:    { synth: (c) => { [880, 1046, 1319].forEach((f, i) => setTimeout(() => synthTone(c, f, 0.15), i * 100)); } },
    free_plays_trigger:{ synth: (c) => { [659, 784, 988].forEach((f, i) => setTimeout(() => synthTone(c, f, 0.22, 'sine', 0.08), i * 130)); } },
    free_play_spin:    { synth: (c) => synthNoiseBurst(c, { dur: 0.12, freqCut: 1200, gain: 0.07, attack: 0.005, release: 0.10 }) },
    feature_complete:  { synth: (c) => { [988, 784, 659, 523].forEach((f, i) => setTimeout(() => synthTone(c, f, 0.14), i * 120)); } },
};

export function createAudioManager({
    soundPack = FALLBACK_SYNTH_PACK,
    storageKey = DEFAULT_STORAGE_KEY,
    initialMuted = null,      // null → read from storage; boolean → force
} = {}) {
    let muted = false;
    if (typeof window !== 'undefined') {
        try {
            const stored = localStorage.getItem(storageKey);
            muted = initialMuted !== null ? Boolean(initialMuted) : stored === '1';
        } catch { /* private mode, etc */ }
    }
    let ctx = null;
    let unlocked = false;
    const loops = new Map();   // event → { source, gainNode }
    const pools = new Map();   // event → HTMLAudioElement[]

    function _ctx() {
        if (typeof window === 'undefined') return null;
        if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
        return ctx;
    }

    async function unlock() {
        if (unlocked) return;
        const c = _ctx();
        if (!c) return;
        try {
            if (c.state === 'suspended') await c.resume();
            // Trigger a silent buffer to satisfy strict iOS
            const buf = c.createBuffer(1, 1, 22050);
            const s = c.createBufferSource();
            s.buffer = buf; s.connect(c.destination); s.start(0);
            unlocked = true;
        } catch { /* fall through */ }
    }

    function _getPool(event) {
        if (pools.has(event)) return pools.get(event);
        const def = soundPack[event];
        if (!def || !def.src) return null;
        const size = Math.max(1, def.poolSize || 3);
        const arr = [];
        for (let i = 0; i < size; i++) {
            const el = new Audio(def.src);
            el.preload = 'auto';
            arr.push(el);
        }
        pools.set(event, arr);
        return arr;
    }

    function play(event, opts = {}) {
        if (muted) return;
        const def = soundPack[event];
        if (!def) return;
        // Synth (fallback pack)
        if (def.synth) {
            try {
                if (!unlocked) return;   // avoid autoplay on mount
                def.synth(_ctx());
            } catch { /* ignore */ }
            return;
        }
        // HTML5 <audio> pooled one-shot
        const pool = _getPool(event);
        if (!pool) return;
        const el = pool.find(a => a.paused || a.ended) || pool[0];
        try {
            el.currentTime = 0;
            el.volume = Math.min(1, (opts.volume ?? def.volume ?? 1.0) * MASTER_VOLUME);
            // Optional per-event playbackRate variation — used by the
            // five reel-stop cues so a SINGLE source WAV can present as
            // five subtly different landings (pitch/gain variation only,
            // per the Premium Audio V1 integration guide).
            const rate = opts.playbackRate ?? def.playbackRate;
            if (rate) el.playbackRate = rate;
            const p = el.play();
            if (p && typeof p.catch === 'function') p.catch(noop);
            // Optional early cutoff — trims a long one-shot so it reads as a
            // short, punchy hit.  A quick ~80ms volume fade avoids a hard
            // click before pausing.  Used by bonus_symbol_land.
            const cutMs = opts.maxDurationMs ?? def.maxDurationMs;
            if (cutMs) {
                setTimeout(() => {
                    try {
                        const startVol = el.volume;
                        let i = 0; const steps = 5;
                        const fade = setInterval(() => {
                            i += 1;
                            el.volume = Math.max(0, startVol * (1 - i / steps));
                            if (i >= steps) {
                                clearInterval(fade);
                                try { el.pause(); el.currentTime = 0; el.volume = startVol; } catch { /* ignore */ }
                            }
                        }, 16);
                    } catch { /* ignore */ }
                }, cutMs);
            }
        } catch { /* ignore */ }
    }

    function loop(event, opts = {}) {
        if (muted) return;
        if (loops.has(event)) return;      // already looping — never stack
        const def = soundPack[event];
        if (!def) return;
        // Persistent WebAudio source (preferred for reel-spin loops).
        // The definition provides a `persistent(ctx)` factory that returns
        // an object with `.stop()` — that graph plays for as long as we
        // want without any timer-based re-triggering (no ticking, ever).
        if (def.persistent) {
            if (!unlocked) return;
            try {
                const rec = def.persistent(_ctx());
                if (rec && typeof rec.stop === 'function') {
                    loops.set(event, rec);
                }
            } catch { /* ignore */ }
            return;
        }
        // (LEGACY) synth-loop path retained for pack backward-compat —
        // still uses a setTimeout tick but is NOT used by the Lucky
        // Ducky default pack anymore.
        if (def.synth) {
            let stopped = false;
            const rec = { stop: () => { stopped = true; } };
            const tick = () => {
                if (stopped || muted) return;
                if (unlocked) def.synth(_ctx());
                setTimeout(tick, 120);
            };
            tick();
            loops.set(event, rec);
            return;
        }
        try {
            const el = new Audio(def.src);
            el.loop = true;
            el.volume = Math.min(1, (opts.volume ?? def.volume ?? 0.5) * MASTER_VOLUME);
            // Optional tempo/pitch bump for looped music (e.g. an energetic,
            // sped-up BETO feature track).  >1 = faster + brighter.
            const rate = opts.playbackRate ?? def.playbackRate;
            if (rate) el.playbackRate = rate;
            const rec = {
                el,
                stop: () => { try { el.pause(); el.currentTime = 0; } catch { /* ignore */ } },
            };
            // Register BEFORE play() so overlapping calls de-dupe, but if
            // play() is blocked (Safari autoplay policy outside a user
            // gesture) we MUST un-register so a later real gesture can retry
            // — otherwise the loop is permanently marked "playing" and the
            // background music never recovers for the whole session.
            loops.set(event, rec);
            const p = el.play();
            if (p && typeof p.catch === 'function') {
                p.catch(() => { if (loops.get(event) === rec) loops.delete(event); });
            }
        } catch { /* ignore */ }
    }

    // Smoothly ramp a live loop's volume down (duck) or back up (restore)
    // over `fadeMs`.  Falls back to an instant volume set if the loop is
    // a persistent WebAudio graph rather than an HTMLAudio element.
    function setLoopVolume(event, targetVolume, fadeMs = 300) {
        const rec = loops.get(event);
        if (!rec || !rec.el) return;
        const el = rec.el;
        const start = el.volume;
        const target = Math.max(0, Math.min(1, targetVolume * MASTER_VOLUME));
        const steps = Math.max(1, Math.round(fadeMs / 30));
        let i = 0;
        const iv = setInterval(() => {
            i += 1;
            const t = i / steps;
            el.volume = start + (target - start) * t;
            if (i >= steps) clearInterval(iv);
        }, 30);
    }

    function stopLoop(event) {
        const rec = loops.get(event);
        if (!rec) return;
        try { rec.stop(); } catch { /* ignore */ }
        loops.delete(event);
    }

    function stopAllLoops() {
        for (const [ev] of Array.from(loops)) stopLoop(ev);
    }

    function setMuted(v) {
        muted = !!v;
        try { localStorage.setItem(storageKey, muted ? '1' : '0'); } catch { /* ignore */ }
        if (muted) { stopAllLoops(); stopAnticipation(null, true); }
    }

    // ── BETO RESPINS anticipation riser (WebAudio) ─────────────────────
    // A rising, filtered saw that builds tension while the deciding reel
    // crawls toward a possible BONUS.  Driven by the game's AUTHORITATIVE
    // land events (never faked).  stopAnticipation(true) resolves with a
    // bright "it landed!" lift; stopAnticipation(false) does a soft
    // downshift release (no failure buzzer).
    let _antiNodes = null;
    function startAnticipation({ dur = 3.2, f0 = 110, f1 = 500, peak = 0.015 } = {}) {
        if (muted || !unlocked) return;
        const c = _ctx(); if (!c) return;
        stopAnticipation(null, true);
        try {
            const osc = c.createOscillator();
            const sub = c.createOscillator();
            const lp  = c.createBiquadFilter();
            const g   = c.createGain();
            osc.type = 'sawtooth'; sub.type = 'sine'; lp.type = 'lowpass';
            const now = c.currentTime;
            // Warm, low build — keep the filter ceiling down so it reads as a
            // deep "winding" swell rather than a high-pitched ping.
            lp.frequency.setValueAtTime(300, now);
            lp.frequency.exponentialRampToValueAtTime(1300, now + dur);
            osc.frequency.setValueAtTime(f0, now);
            osc.frequency.exponentialRampToValueAtTime(f1, now + dur);
            sub.frequency.setValueAtTime(f0 / 2, now);
            sub.frequency.exponentialRampToValueAtTime(f1 / 2, now + dur);
            // Whisper-quiet envelope that SWELLS in, then fades back out on
            // its own by the end of the build so it never becomes an
            // ongoing whine if the reel lingers.
            const atk = Math.min(0.9, dur * 0.35);
            g.gain.setValueAtTime(0.0001, now);
            g.gain.exponentialRampToValueAtTime(peak, now + atk);
            g.gain.exponentialRampToValueAtTime(0.0005, now + dur);
            osc.connect(lp); sub.connect(lp); lp.connect(g); g.connect(c.destination);
            osc.start(now); sub.start(now);
            _antiNodes = { osc, sub, g };
        } catch { /* presentation */ }
    }
    function stopAnticipation(hit = null, silent = false) {
        const nodes = _antiNodes; _antiNodes = null;
        if (!nodes) return;
        const c = _ctx();
        if (!c) { try { nodes.osc.stop(); nodes.sub.stop(); } catch { /* ignore */ } return; }
        const now = c.currentTime;
        try {
            nodes.g.gain.cancelScheduledValues(now);
            nodes.g.gain.setValueAtTime(Math.max(nodes.g.gain.value, 0.0001), now);
            nodes.osc.frequency.cancelScheduledValues(now);
            nodes.osc.frequency.setValueAtTime(nodes.osc.frequency.value, now);
            if (hit && !silent) {
                nodes.osc.frequency.exponentialRampToValueAtTime(820, now + 0.14);
                nodes.g.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
            } else if (!silent) {
                nodes.osc.frequency.exponentialRampToValueAtTime(90, now + 0.4);
                nodes.g.gain.exponentialRampToValueAtTime(0.0001, now + 0.45);
            } else {
                nodes.g.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
            }
            nodes.osc.stop(now + 0.55);
            nodes.sub.stop(now + 0.55);
        } catch { /* presentation */ }
    }

    function isMuted() { return muted; }

    // Convenience: pick a win tier based on payout credits vs wager.
    // Premium Audio V1 tiers (guide):
    //   <1×  bet:  no celebration
    //   1×–<3×    win_small
    //   3×–<10×   win_medium
    //   10×–<25×  win_big
    //   >=25×     win_mega  + subordinate coin_countup
    function playWinBySize({ winCredits, wagerCredits }) {
        if (winCredits <= 0) return;
        const ratio = wagerCredits > 0 ? winCredits / wagerCredits : 0;
        if (ratio < 1) return;                 // sub-bet: no celebration
        if (ratio >= 25) {
            play('win_mega');
            play('win_countup');
        } else if (ratio >= 10) {
            play('win_big');
        } else if (ratio >= 3) {
            play('win_medium');
        } else {
            play('win_small');
        }
    }

    return {
        play, loop, stopLoop, stopAllLoops,
        setLoopVolume,
        startAnticipation, stopAnticipation,
        setMuted, isMuted, unlock,
        playWinBySize,
        get soundPack() { return soundPack; },
    };
}
